import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Card, Col, Form, Row, Spin, Tabs } from 'antd';
import axios from 'axios';
import TextArea from "antd/es/input/TextArea";
import { axiosErrorHandler, showErrorNotification } from '../../../../../swzutils';
import SenderReceiverSelectForm, {} from "../../../components/SenderReceiverSelectForm";
import WaypointSenderReceiverProductValidator from "../../../components/WaypointSenderReceiverProductValidator";
import FormattedAddress from "../../../components/FormattedAddress";
import ConsignmentWaypointResource from "../api/ConsignmentWaypointResource";
import WaypointEditForm, {} from "./WaypointEditForm";
import CachedTenantSettingsRepository from "../../../../../CachedTenantSettingsRepository";
import { SenderReceiverProductCatalogueStatusEnum } from "../../tenants/api/Representation";
import { useAppDispatch } from "../../../../../store";
var AddressMode;
(function (AddressMode) {
    AddressMode["SENDER_RECEIVER"] = "SENDER_RECEIVER";
    AddressMode["NEW_ADDRESS"] = "NEW_ADDRESS";
})(AddressMode || (AddressMode = {}));
const WaypointEditTabs = ({ tenantId, consignmentId, waypoint, isCreate, isPickup, readOnly, editContactsOnly = false, overwriteAddressOnGeolocate, contactNameRequired = false, contactPhoneRequired = false, allowSaveAsSenderReceiver = true }, exportedRef) => {
    const dispatch = useAppDispatch();
    const waypointEditFormRef = useRef(null);
    const senderReceiverFormRef = useRef(null);
    const [changeReasonForm] = Form.useForm();
    const [showSpinner, setShowSpinner] = useState(false);
    const [selectedSenderReceiver, setSelectedSenderReceiver] = useState(null);
    const [senderReceiverLoading, setSenderReceiverLoading] = useState(false);
    const [activeTabKey, setActiveTabKey] = useState(waypoint.sourcedSenderReceiverId ? AddressMode.SENDER_RECEIVER : AddressMode.NEW_ADDRESS);
    const [productCatalogueValid, setProductCatalogueValid] = useState(true);
    const [siteRadius, setSiteRadius] = useState(200);
    const [addressLatLng, setAddressLatLng] = useState(null);
    const [tenantSettings, setTenantSettings] = useState(null);
    useImperativeHandle(exportedRef, () => ({
        validateAndSaveToServer: () => {
            return new Promise((resolve, reject) => {
                validateFields()
                    .then(request => saveWaypoint(request, resolve))
                    .catch(reject);
            });
        },
        validateFields
    }));
    useEffect(() => {
        setActiveTabKey(waypoint.sourcedSenderReceiverId ? AddressMode.SENDER_RECEIVER : AddressMode.NEW_ADDRESS);
        if (waypoint.sourcedSenderReceiverId) {
            fetchSenderReceiver(waypoint.sourcedSenderReceiverId);
        }
        if (waypoint.address.latitude && waypoint.address.longitude) {
            setAddressLatLng({
                lat: waypoint.address.latitude,
                lng: waypoint.address.longitude
            });
        }
        if (waypoint.address.siteRadius) {
            setSiteRadius(waypoint.address.siteRadius);
        }
    }, [waypoint]);
    useEffect(() => {
        loadTenantSettings(tenantId);
    }, [tenantId]);
    const validateFields = () => {
        return new Promise((resolve, reject) => {
            const doValidateAndSaveAddress = (changeReason) => {
                if (activeTabKey === AddressMode.NEW_ADDRESS) {
                    validateAndSaveNewAddress(changeReason, resolve, reject);
                }
                else if (activeTabKey === AddressMode.SENDER_RECEIVER) {
                    validateAndSaveSenderReceiver(changeReason, resolve, reject);
                }
            };
            if (showChangeReasonForm()) {
                changeReasonForm.validateFields()
                    .then(values => doValidateAndSaveAddress(values.changeReason))
                    .catch(console.error); // Required for antd
            }
            else {
                doValidateAndSaveAddress(null);
            }
        });
    };
    const loadTenantSettings = (tenantId) => {
        CachedTenantSettingsRepository.getSettingsForTenant(tenantId, dispatch)
            .then(settings => setTenantSettings(settings))
            .catch(axiosErrorHandler);
    };
    const validateAndSaveNewAddress = (changeReason, resolve, reject) => {
        waypointEditFormRef.current?.validateFields().then((formValues) => {
            if (!siteRadius || !addressLatLng) {
                showErrorNotification("Please position centre of the circle to the location of this address");
                return;
            }
            const waypointUpdateRequest = {
                companyName: formValues.companyName,
                contactName: formValues.contactName,
                contactTelephone: formValues.contactTelephone,
                contactEmail: formValues.contactEmail,
                uhfChannelLoader: formValues.uhfChannelLoader,
                uhfChannelOffice: formValues.uhfChannelOffice,
                version: waypoint?.waypointVersion,
                sourcedSenderReceiverId: null,
                changeReason,
                address: {
                    unitOrFlat: formValues.unitOrFlat,
                    street: formValues.street,
                    suburb: formValues.suburb,
                    state: formValues.state,
                    postcode: formValues.postcode,
                    countryCode: formValues.countryCode,
                    latitude: addressLatLng.lat,
                    longitude: addressLatLng.lng,
                    siteRadius: siteRadius
                }
            };
            resolve(waypointUpdateRequest);
        }).catch(reject);
    };
    const validateAndSaveSenderReceiver = (changeReason, resolve, reject) => {
        senderReceiverFormRef.current?.validateFields().then(( /*formValues*/) => {
            if (isPickup && !productCatalogueValid) {
                showErrorNotification("The selected saved location does not have the required products or is not open on applicable date and time. Check warning for more details.");
                return;
            }
            if (selectedSenderReceiver && selectedSenderReceiver.address) {
                const waypointUpdateRequest = {
                    companyName: selectedSenderReceiver.companyName,
                    contactName: selectedSenderReceiver.contactName,
                    contactTelephone: selectedSenderReceiver.contactTelephone,
                    contactEmail: selectedSenderReceiver.contactEmail,
                    uhfChannelLoader: selectedSenderReceiver.uhfChannelLoader,
                    uhfChannelOffice: selectedSenderReceiver.uhfChannelOffice,
                    version: waypoint?.waypointVersion,
                    sourcedSenderReceiverId: selectedSenderReceiver.id,
                    changeReason,
                    address: {
                        unitOrFlat: selectedSenderReceiver.address.unitOrFlat,
                        street: selectedSenderReceiver.address.street,
                        suburb: selectedSenderReceiver.address.suburb,
                        state: selectedSenderReceiver.address.state,
                        postcode: selectedSenderReceiver.address.postcode,
                        countryCode: selectedSenderReceiver.address.countryCode,
                        latitude: selectedSenderReceiver.address.latitude,
                        longitude: selectedSenderReceiver.address.longitude
                    }
                };
                resolve(waypointUpdateRequest);
            }
        }).catch(reject);
    };
    const saveWaypoint = (waypointUpdateRequest, onSaveSuccess) => {
        setShowSpinner(true);
        if (editContactsOnly) {
            const patchContactsRequest = {
                companyName: waypointUpdateRequest.companyName,
                contactName: waypointUpdateRequest.contactName,
                contactTelephone: waypointUpdateRequest.contactTelephone,
                contactEmail: waypointUpdateRequest.contactEmail,
                version: waypointUpdateRequest.version,
                uhfChannelLoader: waypointUpdateRequest.uhfChannelLoader,
                uhfChannelOffice: waypointUpdateRequest.uhfChannelOffice
            };
            ConsignmentWaypointResource.patchContacts(consignmentId, waypoint.waypointId, patchContactsRequest, () => onSaveSuccess(waypointUpdateRequest), axiosErrorHandler, () => setShowSpinner(false));
        }
        else {
            ConsignmentWaypointResource.update(consignmentId, waypoint.waypointId, waypointUpdateRequest, () => onSaveSuccess(waypointUpdateRequest), axiosErrorHandler, () => setShowSpinner(false));
        }
    };
    const onAddressLocationChanged = (mapCenter) => {
        setAddressLatLng(mapCenter);
    };
    const onSiteRadiusChanged = (radius) => {
        setSiteRadius(radius);
    };
    const onSenderReceiverSelected = (senderReceiver) => {
        setSelectedSenderReceiver(senderReceiver);
    };
    const fetchSenderReceiver = (senderReceiverId) => {
        setSenderReceiverLoading(true);
        axios.get(`/admin/v1/sender-receivers/${senderReceiverId}`)
            .then((response) => setSelectedSenderReceiver(response.data))
            .catch(axiosErrorHandler)
            .finally(() => setSenderReceiverLoading(false));
    };
    const onChangeTab = (activeTabKey) => {
        setActiveTabKey(activeTabKey);
    };
    const showChangeReasonForm = () => {
        return isPickup
            && !isCreate
            && !readOnly
            && tenantSettings?.senderReceiverProductCatalogue !== SenderReceiverProductCatalogueStatusEnum.DISABLED;
    };
    const requireChangeReason = () => {
        return showChangeReasonForm()
            && tenantSettings?.senderReceiverProductCatalogue === SenderReceiverProductCatalogueStatusEnum.ENFORCED;
    };
    const onAddressSavedAsSenderReceiver = (sr) => {
        setActiveTabKey(AddressMode.SENDER_RECEIVER);
        setSelectedSenderReceiver(sr);
    };
    return (_jsxs(Spin, { spinning: showSpinner, children: [_jsx(Tabs, { animated: true, activeKey: activeTabKey, onChange: onChangeTab, type: "card", style: { marginTop: 20 }, items: [{
                        key: AddressMode.NEW_ADDRESS,
                        label: 'Enter Address',
                        disabled: readOnly && activeTabKey == AddressMode.SENDER_RECEIVER,
                        children: _jsx(WaypointEditForm, { ref: waypointEditFormRef, tenantId: tenantId, waypoint: waypoint, readOnly: readOnly, editContactsOnly: editContactsOnly, overwriteAddressOnGeolocate: overwriteAddressOnGeolocate, onSiteRadiusChanged: onSiteRadiusChanged, onLocationChanged: onAddressLocationChanged, contactNameRequired: contactNameRequired, contactPhoneRequired: contactPhoneRequired, allowSaveAsSenderReceiver: allowSaveAsSenderReceiver, onSavedAsSenderReceiver: onAddressSavedAsSenderReceiver })
                    }, {
                        key: AddressMode.SENDER_RECEIVER,
                        label: 'Choose Saved Location',
                        disabled: readOnly && activeTabKey == AddressMode.NEW_ADDRESS,
                        children: _jsxs(Row, { gutter: 10, className: "no-margin", children: [_jsx(Col, { lg: 15, children: _jsxs(Card, { title: "Saved Location", type: "inner", variant: "borderless", children: [_jsx(SenderReceiverSelectForm, { tenantId: tenantId, disabled: readOnly, allowClear: false, senderReceiverId: selectedSenderReceiver?.id, onSenderReceiverSelected: onSenderReceiverSelected, ref: senderReceiverFormRef, required: true, onFinish: () => { } }), isPickup && !readOnly && waypoint.waypointId && consignmentId &&
                                                _jsx(WaypointSenderReceiverProductValidator, { consignmentId: consignmentId, waypointId: waypoint.waypointId, senderReceiverId: selectedSenderReceiver?.id, tenantSettings: tenantSettings, onSenderReceiverSelected: (senderReceiver) => {
                                                        setSelectedSenderReceiver(senderReceiver);
                                                        onSenderReceiverSelected(senderReceiver);
                                                    }, onValidationChanged: setProductCatalogueValid })] }) }), _jsx(Col, { lg: 9, children: _jsx(Card, { title: "Address", type: "inner", variant: "borderless", children: _jsx(Spin, { spinning: senderReceiverLoading, children: selectedSenderReceiver &&
                                                _jsx(FormattedAddress, { companyName: selectedSenderReceiver.companyName, contactName: selectedSenderReceiver.contactName, contactTelephone: selectedSenderReceiver.contactTelephone, contactEmail: selectedSenderReceiver.contactEmail, address: selectedSenderReceiver.address, uhfChannelOffice: selectedSenderReceiver.uhfChannelOffice, uhfChannelLoader: selectedSenderReceiver.uhfChannelLoader }) }) }) })] })
                    }
                ] }), (showChangeReasonForm()) &&
                _jsx(_Fragment, { children: _jsx(Card, { title: "Reason", type: "inner", variant: "borderless", children: _jsx(Form, { form: changeReasonForm, layout: "vertical", children: _jsx(Form.Item, { name: "changeReason", label: "Change Reason", rules: [
                                    {
                                        required: requireChangeReason()
                                    }
                                ], children: _jsx(TextArea, {}) }) }) }) })] }));
};
export default React.forwardRef(WaypointEditTabs);
